import { ReferralResultsLoader } from "./ReferralResultsLoader";
import { ReferralConnectionBadge } from "./ReferralFirmCells";
import { ReferralFirmCard } from "./ReferralFirmCard";
import {
  matchesReferralNetworkFilters,
  ReferralNetworkFilters,
  type ReferralNetworkFilterValues,
} from "./ReferralNetworkFilters";
import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useUser } from "@/components/UserProvider";
import {
  ActionEmptyState,
  ActionConfirmationModal,
  AppModal,
  AppPageShell,
  Button,
  LoadingSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import {
  partnerFirmsQueryOptions,
  partnerRequestsQueryOptions,
  useRemovePartnerFirm,
  useRespondToPartnerRequest,
} from "@/lib/data/react-query/firms/referral-partners";
import type { PartnerFirm } from "@/lib/data/requests/firms/referral-partners";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { formatDate } from "@/lib/utils/format";
import { ReferralDirectory } from "./ReferralDirectory";

const partnerTabs = [
  { value: "partners", label: "Partners" },
  { value: "incoming", label: "Incoming requests" },
  { value: "outgoing", label: "Outgoing requests" },
] as const;

type PartnerTab = (typeof partnerTabs)[number]["value"];

const emptyFilters: ReferralNetworkFilterValues = {
  practiceAreaIds: [],
  search: "",
  serviceAreas: [],
};

const emptyContent = {
  partners: {
    title: "No partner firms yet",
    description: "Find a firm and send a partner request to grow your network.",
  },
  incoming: {
    title: "No incoming requests",
    description: "Requests from other firms will appear here.",
  },
  outgoing: {
    title: "No outgoing requests",
    description: "Partner requests you send will appear here until resolved.",
  },
};

export function PartnerFirmsPage() {
  const [activeTab, setActiveTab] = useState<PartnerTab>("partners");
  const [findFirmOpen, setFindFirmOpen] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [partnerToRemove, setPartnerToRemove] = useState<PartnerFirm | null>(
    null,
  );
  const { hasActionPermission } = useUser();
  const partners = useInfiniteQuery(partnerFirmsQueryOptions());
  const incoming = useInfiniteQuery(partnerRequestsQueryOptions("INCOMING"));
  const outgoing = useInfiniteQuery(partnerRequestsQueryOptions("OUTGOING"));
  const respond = useRespondToPartnerRequest();
  const removePartner = useRemovePartnerFirm();
  const queries = { partners, incoming, outgoing };
  const query = queries[activeTab];
  const partnerEntries =
    partners.data?.pages.flatMap((page) => page.data) ?? [];
  const incomingEntries =
    incoming.data?.pages.flatMap((page) => page.data) ?? [];
  const outgoingEntries =
    outgoing.data?.pages.flatMap((page) => page.data) ?? [];
  const filteredPartnerEntries = partnerEntries.filter((entry) =>
    matchesReferralNetworkFilters(entry.connectedFirm, filters),
  );
  const filteredIncomingEntries = incomingEntries.filter((entry) =>
    matchesReferralNetworkFilters(entry.counterpartyFirm, filters),
  );
  const filteredOutgoingEntries = outgoingEntries.filter((entry) =>
    matchesReferralNetworkFilters(entry.counterpartyFirm, filters),
  );
  const filteredEntryCount =
    activeTab === "partners"
      ? filteredPartnerEntries.length
      : activeTab === "incoming"
        ? filteredIncomingEntries.length
        : filteredOutgoingEntries.length;
  const counts: Record<PartnerTab, number> = {
    partners:
      partners.data?.pages[0]?.pagination?.count ?? partnerEntries.length,
    incoming:
      incoming.data?.pages[0]?.pagination?.count ?? incomingEntries.length,
    outgoing:
      outgoing.data?.pages[0]?.pagination?.count ?? outgoingEntries.length,
  };
  const hasFilters = Boolean(
    filters.search.trim() ||
      filters.practiceAreaIds.length ||
      filters.serviceAreas.length,
  );
  const canRespond = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmReferralConnectionRequest,
    ACTION_PERMISSION_VALUES.update,
  );
  const canRemove = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmReferralNetworkConnection,
    ACTION_PERMISSION_VALUES.delete,
  );

  return (
    <AppPageShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Partner firms</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                setFindFirmOpen(true);
              }}
            >
              Find a firm
            </Button>
          </div>
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            const tab = partnerTabs.find((item) => item.value === value);
            if (tab) setActiveTab(tab.value);
          }}
        >
          <div className="overflow-x-auto border-b">
            <TabsList>
              {partnerTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                  <span className="bg-muted text-muted-foreground ml-2 min-w-5 rounded-full px-1.5 py-0.5 text-center text-xs leading-none">
                    {counts[tab.value]}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value={activeTab} className="mt-4 space-y-3">
            <ReferralNetworkFilters {...filters} onChange={setFilters} />
            {query.isPending ? (
              <div
                role="status"
                className="text-muted-foreground flex items-center gap-2 rounded-xl border p-4 text-sm"
              >
                <LoadingSpinner /> Loading{" "}
                {activeTab === "partners" ? "partners" : "requests"}...
              </div>
            ) : query.isError && !query.data ? (
              <div role="alert">
                <ActionEmptyState
                  align="left"
                  className="gap-2 p-4 [&_.text-xl]:text-base"
                  title={
                    activeTab === "partners"
                      ? "Could not load partners"
                      : "Could not load requests"
                  }
                  description="Please try again."
                  actions={
                    <Button
                      variant="outline"
                      onClick={() => {
                        void query.refetch();
                      }}
                    >
                      Retry
                    </Button>
                  }
                />
              </div>
            ) : filteredEntryCount === 0 ? (
              <ActionEmptyState
                align="left"
                className="gap-2 p-4 [&_.text-xl]:text-base"
                {...(hasFilters
                  ? {
                      title: "No firms found",
                      description: "Try another search or adjust your filters.",
                    }
                  : emptyContent[activeTab])}
              />
            ) : activeTab === "partners" ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] gap-4">
                {filteredPartnerEntries.map((entry) => (
                  <ReferralFirmCard
                    key={entry.id}
                    firm={entry.connectedFirm}
                    footer={
                      <>
                        <div className="flex flex-col items-start gap-1.5">
                          <ReferralConnectionBadge status="partner" />
                          <span className="text-muted-foreground text-xs">
                            Connected {formatDate(entry.dateCreated)}
                          </span>
                        </div>
                        {canRemove && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setPartnerToRemove(entry);
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </>
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] gap-4">
                {(activeTab === "incoming"
                  ? filteredIncomingEntries
                  : filteredOutgoingEntries
                ).map((entry) => {
                  const isThisRequestPending =
                    respond.isPending &&
                    respond.variables.requestId === entry.id;
                  return (
                    <ReferralFirmCard
                      key={entry.id}
                      firm={entry.counterpartyFirm}
                      footer={
                        <>
                          <div className="flex flex-col items-start gap-1.5">
                            {activeTab === "outgoing" && (
                              <ReferralConnectionBadge status="outgoing" />
                            )}
                            <span className="text-muted-foreground text-xs">
                              Requested {formatDate(entry.dateCreated)}
                            </span>
                          </div>
                          {canRespond && (
                            <div className="flex flex-wrap justify-end gap-2">
                              {activeTab === "incoming" ? (
                                <>
                                  <Button
                                    size="sm"
                                    disabled={isThisRequestPending}
                                    onClick={() => {
                                      respond.mutate({
                                        requestId: entry.id,
                                        action: "accept",
                                      });
                                    }}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={isThisRequestPending}
                                    onClick={() => {
                                      respond.mutate({
                                        requestId: entry.id,
                                        action: "decline",
                                      });
                                    }}
                                  >
                                    Decline
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={isThisRequestPending}
                                  onClick={() => {
                                    respond.mutate({
                                      requestId: entry.id,
                                      action: "cancel",
                                    });
                                  }}
                                >
                                  Cancel request
                                </Button>
                              )}
                            </div>
                          )}
                        </>
                      }
                    />
                  );
                })}
              </div>
            )}
            {query.isError && query.data && !query.isFetchNextPageError && (
              <p role="alert" className="text-sm">
                Could not refresh the list.{" "}
                <Button
                  variant="outline"
                  onClick={() => {
                    void query.refetch();
                  }}
                >
                  Retry
                </Button>
              </p>
            )}
            <ReferralResultsLoader
              key={activeTab}
              hasNextPage={Boolean(query.hasNextPage)}
              isFetching={query.isFetching}
              isError={query.isError}
              fetchNextPage={query.fetchNextPage}
            />
          </TabsContent>
        </Tabs>
      </div>
      <AppModal
        open={findFirmOpen}
        onOpenChange={setFindFirmOpen}
        title="Find a firm"
        size="4xl"
        className="h-[min(88dvh,880px)] max-w-[1440px]"
        bodyContained
      >
        <ReferralDirectory embedded />
      </AppModal>
      <ActionConfirmationModal
        open={Boolean(partnerToRemove)}
        setOpen={(open) => {
          if (!open) setPartnerToRemove(null);
        }}
        title={`Remove ${partnerToRemove?.connectedFirm.name ?? "this firm"} from your partner network?`}
        description="This removes the referral partnership for both firms. You can send a new partner request later."
        buttonText="Remove partner"
        isActionPending={removePartner.isPending}
        afterActionFn={async () => {
          if (!partnerToRemove) return;
          await removePartner.mutateAsync(partnerToRemove.id);
          setPartnerToRemove(null);
        }}
      />
    </AppPageShell>
  );
}
